// Floor-plan PNG export — draws each floor's plan with numbered division
// markers and a legend underneath (devices per division), sized for the
// customer to hand to the montador. Pure canvas, no extra dependencies.

import type { Floor } from '../store/wizardStore';

export interface ExportDivision {
  number: number; // global marker number across floors
  label: string;
  lines: string[]; // device summaries for the legend
}

export interface ExportFloor {
  floor: Floor;
  planImage: HTMLImageElement | null;
  divisions: Map<string, ExportDivision>; // divisionId → entry (placed on this floor)
}

const W = 1400;
const PAD = 48;
const MARKER_R = 26;
const BRAND = '#1E40AF';
const INK = '#0F172A';
const MUTED = '#64748B';
const LINE = '#E2E8F0';

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('plan image failed to load'));
    img.src = src;
  });
}

function planHeight(img: HTMLImageElement | null): number {
  const w = W - PAD * 2;
  if (!img) return Math.round(w * 0.6);
  return Math.round((img.naturalHeight / img.naturalWidth) * w);
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (ctx.measureText(next).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  }
  if (current) lines.push(current);
  return lines;
}

function drawMarker(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  n: number,
): void {
  ctx.beginPath();
  ctx.arc(x, y, MARKER_R, 0, Math.PI * 2);
  ctx.fillStyle = BRAND;
  ctx.fill();
  ctx.lineWidth = 3;
  ctx.strokeStyle = '#ffffff';
  ctx.stroke();
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 22px Inter, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(n), x, y + 1);
}

// Measure-then-draw: first pass computes total height, second pass renders.
export function renderPlanPng(
  floors: ExportFloor[],
  legend: ExportDivision[],
  title: string,
): Promise<Blob> {
  const probe = document.createElement('canvas').getContext('2d');
  if (!probe) return Promise.reject(new Error('canvas unavailable'));

  // ── measure ──
  let height = PAD; // top
  height += 56; // title
  for (const f of floors) {
    height += 40; // floor label
    height += planHeight(f.planImage) + 24;
  }
  height += 48; // legend header
  probe.font = '20px Inter, system-ui, sans-serif';
  const legendWidth = W - PAD * 2 - 72;
  const legendLineCounts = legend.map((d) => {
    const deviceLines = d.lines.length > 0 ? d.lines : ['(sem aparelhos definidos)'];
    return deviceLines.reduce(
      (sum, l) => sum + wrapText(probe, l, legendWidth).length,
      0,
    );
  });
  for (const count of legendLineCounts) height += 34 + count * 26 + 10;
  height += PAD;

  // ── draw ──
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.reject(new Error('canvas unavailable'));

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, height);

  let y = PAD;
  ctx.fillStyle = INK;
  ctx.font = 'bold 32px Inter, system-ui, sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText(title, PAD, y);
  y += 56;

  for (const f of floors) {
    ctx.fillStyle = MUTED;
    ctx.font = 'bold 22px Inter, system-ui, sans-serif';
    ctx.fillText(f.floor.label.toUpperCase(), PAD, y);
    y += 40;

    const planW = W - PAD * 2;
    const planH = planHeight(f.planImage);
    if (f.planImage) {
      ctx.drawImage(f.planImage, PAD, y, planW, planH);
    } else {
      ctx.fillStyle = '#f4f4f6';
      ctx.fillRect(PAD, y, planW, planH);
    }
    ctx.strokeStyle = LINE;
    ctx.lineWidth = 2;
    ctx.strokeRect(PAD, y, planW, planH);

    for (const [divId, entry] of f.divisions) {
      const pos = f.floor.placements[divId];
      if (!pos) continue;
      drawMarker(ctx, PAD + pos.x * planW, y + pos.y * planH, entry.number);
    }
    y += planH + 24;
  }

  // legend
  ctx.fillStyle = INK;
  ctx.font = 'bold 24px Inter, system-ui, sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText('Legenda', PAD, y);
  y += 48;

  for (const d of legend) {
    drawMarker(ctx, PAD + MARKER_R, y + MARKER_R - 6, d.number);
    ctx.fillStyle = INK;
    ctx.font = 'bold 22px Inter, system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(d.label, PAD + 72, y);
    y += 34;

    ctx.fillStyle = MUTED;
    ctx.font = '20px Inter, system-ui, sans-serif';
    const deviceLines = d.lines.length > 0 ? d.lines : ['(sem aparelhos definidos)'];
    for (const line of deviceLines) {
      for (const wrapped of wrapText(ctx, line, W - PAD * 2 - 72)) {
        ctx.fillText(`· ${wrapped}`, PAD + 72, y);
        y += 26;
      }
    }
    y += 10;
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('toBlob failed'));
    }, 'image/png');
  });
}
